import {useCallback, useState} from 'react'
import {View} from 'react-native'
import {Image} from 'expo-image'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import {formatFileSize} from '#/state/matrix/mediaConfig'
import {type AttachmentToPick} from '#/state/matrix/types'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {Text} from '#/components/Typography'

export function SecureUploadConfirmDialog({
  control,
  attachments,
  onConfirm,
  onCancel,
}: {
  control: Dialog.DialogControlProps
  attachments: AttachmentToPick[]
  onConfirm: (attachments: AttachmentToPick[]) => void
  onCancel: () => void
}) {
  const {_} = useLingui()
  const t = useTheme()
  const [currentIndex, setCurrentIndex] = useState(0)

  const current = attachments[currentIndex]
  const isMultiple = attachments.length > 1
  const isLast = currentIndex === attachments.length - 1
  const isImage = current?.mime.startsWith('image/')

  const onUpload = useCallback(() => {
    if (isLast) {
      onConfirm(attachments)
      control.close()
    } else {
      setCurrentIndex(prev => prev + 1)
    }
  }, [isLast, attachments, onConfirm, control])

  const onUploadAll = useCallback(() => {
    onConfirm(attachments)
    control.close()
  }, [attachments, onConfirm, control])

  const onDismiss = useCallback(() => {
    setCurrentIndex(0)
    onCancel()
  }, [onCancel])

  if (!current) return null

  return (
    <Dialog.Outer
      control={control}
      onClose={onDismiss}
      testID="uploadConfirmDialog">
      <Dialog.Handle />
      <Dialog.Inner label={_(msg`Confirm upload`)}>
        <View style={[a.gap_md]}>
          {/* File counter */}
          {isMultiple && (
            <Text
              style={[a.text_sm, t.atoms.text_contrast_medium, a.text_center]}>
              <Trans>
                File {currentIndex + 1} of {attachments.length}
              </Trans>
            </Text>
          )}

          {/* Preview */}
          {isImage ? (
            <View
              style={[
                a.rounded_md,
                a.overflow_hidden,
                a.align_center,
                {maxHeight: 300},
              ]}>
              <Image
                source={{uri: current.uri}}
                style={{
                  width: '100%',
                  aspectRatio:
                    current.width && current.height
                      ? current.width / current.height
                      : 1,
                  maxHeight: 300,
                }}
                contentFit="contain"
                accessibilityIgnoresInvertColors
              />
            </View>
          ) : (
            <View
              style={[
                a.rounded_md,
                a.p_lg,
                a.align_center,
                t.atoms.bg_contrast_25,
              ]}>
              <Text style={[a.text_2xl]}>📄</Text>
            </View>
          )}

          {/* File info */}
          <View style={[a.gap_2xs]}>
            <Text style={[a.text_md, a.font_bold]} numberOfLines={1}>
              {current.name}
            </Text>
            <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
              {formatFileSize(current.size)}
              {current.width > 0 && current.height > 0
                ? ` · ${current.width}×${current.height}`
                : ''}
            </Text>
          </View>

          {/* Actions */}
          <View style={[a.flex_row, a.gap_sm, a.justify_end]}>
            <Button
              variant="ghost"
              color="secondary"
              size="small"
              label={_(msg`Cancel`)}
              onPress={() => {
                control.close()
                onDismiss()
              }}>
              <ButtonText>
                <Trans>Cancel</Trans>
              </ButtonText>
            </Button>
            {isMultiple && !isLast && (
              <Button
                variant="ghost"
                color="primary"
                size="small"
                label={_(msg`Upload all`)}
                onPress={onUploadAll}>
                <ButtonText>
                  <Trans>Upload All</Trans>
                </ButtonText>
              </Button>
            )}
            <Button
              variant="solid"
              color="primary"
              size="small"
              label={isLast ? _(msg`Upload`) : _(msg`Next`)}
              onPress={onUpload}>
              <ButtonText>
                {isLast ? <Trans>Upload</Trans> : <Trans>Next</Trans>}
              </ButtonText>
            </Button>
          </View>
        </View>
      </Dialog.Inner>
    </Dialog.Outer>
  )
}
